import * as React from 'react';

import type { KanbanContextValue, KanbanSelectionContextValue } from './types';


const KanbanContext = React.createContext<KanbanContextValue<any, any> | null>(
  null,
);

export function useKanbanDrag<TItem, TColumn>() {
  const ctx = React.useContext(
    KanbanContext,
  ) as KanbanContextValue<TItem, TColumn> | null;
  if (!ctx) throw new Error('useKanbanDrag must be used within a KanbanBoard');
  return ctx;
}

export const KanbanProvider = KanbanContext.Provider;

// Selection rides its own context so a toggle re-renders only the mounted
// cards: KanbanContext's identity is reserved for drag/collapse state, and
// React.memo'd columns must not pay for a checkbox click.

const KanbanSelectionContext =
  React.createContext<KanbanSelectionContextValue<any> | null>(null);

/** `null` when the board has selection disabled (no `selectedItemIds`). */
export function useKanbanSelection<TItem>() {
  return React.useContext(
    KanbanSelectionContext,
  ) as KanbanSelectionContextValue<TItem> | null;
}

export const KanbanSelectionProvider = KanbanSelectionContext.Provider;

// The column's full ordered slot list (unloaded slots are `undefined`),
// resolved lazily on demand — shift+click range selection reads the order
// through the card, which otherwise only knows its own item.

const KanbanColumnItemsContext = React.createContext<
  (() => readonly unknown[]) | null
>(null);

/** `null` outside a column body (e.g. the drag overlay). */
export function useKanbanColumnItems<TItem>() {
  return React.useContext(KanbanColumnItemsContext) as
    | (() => readonly (TItem | undefined)[])
    | null;
}

export const KanbanColumnItemsProvider = KanbanColumnItemsContext.Provider;
